import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Search, Send, Trash2, MessageCircle, Sparkles, Shield, Gavel, Loader2, Moon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import posthog from '../lib/posthog';
import IslamojiPicker from './IslamojiPicker';
import { useIslamojis } from '../hooks/useIslamojis';
import { renderLineWithIslamojis, getSoloIslamoji } from '../lib/islamojiRender';

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const UserBadges = ({ user, size = 12 }) => (
  <span className="inline-flex items-center gap-1 ml-1">
    {user?.is_admin && <Shield className="text-primary" style={{ width: size, height: size }} />}
    {!user?.is_admin && user?.is_moderator && (
      <Gavel style={{ width: size, height: size, color: '#7d6dab' }} />
    )}
    {user?.is_premium && !user?.is_admin && (
      <Sparkles className="text-yellow-500" style={{ width: size, height: size }} />
    )}
  </span>
);

const MessagesPage = ({ onBack, presetTargetId }) => {
  const { profile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [showIslamojis, setShowIslamojis] = useState(false);
  const draftInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const islamojis = useIslamojis();
  const islamojiMap = React.useMemo(() => {
    const map = {};
    islamojis.forEach(item => { if (item.type === 'emoji') map[item.name.toLowerCase()] = item; });
    return map;
  }, [islamojis]);

  const loadConversations = useCallback(async () => {
    if (!profile?.id) return;
    setLoadingConvs(true);
    try {
      const res = await api.listConversations(profile.id);
      setConversations(res.data || []);
    } catch { setConversations([]); }
    setLoadingConvs(false);
  }, [profile?.id]);

  const openConversation = useCallback(async (targetId) => {
    try {
      const res = await api.openConversation(targetId);
      const conv = res.data;
      // Refresh list to ensure it appears
      await loadConversations();
      setActiveConv(conv);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Cannot open conversation');
    }
  }, [loadConversations]);

  // Initial load + handle preset target
  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (presetTargetId && profile?.id && presetTargetId !== profile.id) {
      openConversation(presetTargetId);
    }
  }, [presetTargetId, profile?.id, openConversation]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (!activeConv?.id) { setMessages([]); return; }
    setLoadingMsgs(true);
    api.getMessages(activeConv.id).then(r => {
      setMessages(r.data || []);
      // After read, refresh unread badges
      loadConversations();
    }).catch(() => setMessages([])).finally(() => setLoadingMsgs(false));
  }, [activeConv?.id, loadConversations]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Search debounce
  useEffect(() => {
    if (!searchQuery.trim() || !profile?.id) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.searchMessageableUsers(profile.id, searchQuery.trim());
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, profile?.id]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !activeConv?.id || sending) return;
    if (text.length > 1000) { alert('Message too long (max 1000 characters)'); return; }
    setSending(true);
    try {
      const res = await api.sendMessage(activeConv.id, text);
      posthog.capture('direct_message_sent');
      setMessages(prev => [...prev, res.data]);
      setDraft('');
      setShowIslamojis(false);
      loadConversations();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  const handleSelectIslamojiEmoji = (item) => {
    const shortcode = `:${item.name}:`;
    const el = draftInputRef.current;
    if (el) {
      const start = el.selectionStart ?? draft.length;
      const end = el.selectionEnd ?? draft.length;
      const next = draft.slice(0, start) + shortcode + draft.slice(end);
      setDraft(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + shortcode.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setDraft(d => d + shortcode);
    }
  };

  const handleDeleteConversation = async (conv) => {
    if (!window.confirm('Delete this conversation from your view?')) return;
    try {
      await api.deleteConversation(conv.id);
      if (activeConv?.id === conv.id) setActiveConv(null);
      await loadConversations();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6" data-testid="messages-page">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full" data-testid="messages-back-btn">
          <ChevronLeft className="w-6 h-6 text-primary" />
        </button>
        <h1 className="font-heading text-2xl font-semibold text-primary">Messages</h1>
      </div>

      {!profile?.is_premium && (
        <div className="bg-primary/10 border border-primary/20 rounded-arch-lg p-4 text-sm text-text-main flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Messaging is a Sirati+ feature.
          <button onClick={onBack} className="ml-auto text-primary font-medium hover:underline">Back</button>
        </div>
      )}

      {profile?.is_premium && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-[20rem_1fr] gap-4 h-[calc(100vh-12rem)] glass-card rounded-arch-xl overflow-hidden">
          {/* LEFT — conversation list + search */}
          <aside className="border-r border-primary/10 flex flex-col min-h-0">
            <div className="p-3 border-b border-primary/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Find someone to message…" maxLength={30}
                  className="w-full pl-9 pr-3 py-2 rounded-full bg-white/70 dark:bg-white text-sm border border-primary/15 focus:ring-2 focus:ring-primary/40 dark:text-gray-800"
                  data-testid="messages-search-input" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {searchQuery.trim() && (
                <div>
                  {searchResults.length === 0 ? (
                    <p className="px-4 py-6 text-xs text-text-muted">No matching users you can message.</p>
                  ) : (
                    searchResults.map(u => (
                      <button key={u.id} onClick={() => { openConversation(u.id); setSearchQuery(''); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 border-b border-primary/5 text-left"
                        data-testid={`message-search-result-${u.id}`}>
                        <img src={u.avatar_url || defaultAvatar(u.gender)}
                          className="w-9 h-9 rounded-full border border-primary/20" alt={u.display_name || 'User'} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-main truncate flex items-center">
                            {u.display_name} <UserBadges user={u} />
                          </p>
                          <p className="text-[11px] text-text-muted">Start conversation</p>
                        </div>
                      </button>
                    ))
                  )}
                  <div className="border-t border-primary/10" />
                </div>
              )}

              {loadingConvs ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : conversations.length === 0 ? (
                <p className="px-4 py-8 text-xs text-text-muted text-center">No conversations yet. Search for someone above to get started.</p>
              ) : (
                conversations.map(c => {
                  const isActive = activeConv?.id === c.id;
                  const unread = c.unread_count > 0;
                  return (
                    <button key={c.id} onClick={() => setActiveConv(c)}
                      className={`w-full flex items-start gap-3 px-4 py-3 border-b border-primary/5 text-left transition-colors ${isActive ? 'bg-primary/10' : 'hover:bg-primary/5'}`}
                      data-testid={`conversation-${c.id}`}>
                      <img src={c.other_user?.avatar_url || defaultAvatar(c.other_user?.gender)}
                        className="w-10 h-10 rounded-full border border-primary/20 flex-shrink-0" alt={c.other_user?.display_name || 'User'} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm truncate flex items-center ${unread ? 'font-bold text-text-main' : 'font-medium text-text-main'}`}>
                            {c.other_user?.display_name} <UserBadges user={c.other_user} />
                          </p>
                          <span className="text-[10px] text-text-muted flex-shrink-0">{formatTime(c.last_message_at)}</span>
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${unread ? 'font-semibold text-text-main' : 'text-text-muted'}`}>
                          {c.last_message_sender_id === profile.id ? 'You: ' : ''}{c.last_message || 'No messages yet'}
                        </p>
                      </div>
                      {unread && (
                        <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded-full" data-testid={`unread-badge-${c.id}`}>
                          {c.unread_count}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* RIGHT — active conversation */}
          <section className="flex flex-col min-h-0">
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center text-text-muted text-sm">
                <div className="text-center">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Select a conversation
                </div>
              </div>
            ) : (
              <>
                <header className="px-5 py-3 border-b border-primary/10 flex items-center gap-3">
                  <img src={activeConv.other_user?.avatar_url || defaultAvatar(activeConv.other_user?.gender)}
                    className="w-9 h-9 rounded-full border border-primary/20" alt={activeConv.other_user?.display_name || 'User'} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-main flex items-center" data-testid="active-conv-name">
                      {activeConv.other_user?.display_name} <UserBadges user={activeConv.other_user} />
                    </p>
                  </div>
                  <button onClick={() => handleDeleteConversation(activeConv)}
                    className="p-2 rounded-full hover:bg-red-50 text-red-500"
                    data-testid="delete-conversation-btn"
                    title="Delete conversation">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </header>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="messages-thread">
                  {loadingMsgs ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                  ) : messages.length === 0 ? (
                    <p className="text-center text-xs text-text-muted py-8">Say something kind — start the conversation.</p>
                  ) : (
                    messages.map(m => {
                      const mine = m.sender_id === profile.id;
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? 'bg-primary text-white rounded-br-sm' : 'bg-white/70 dark:bg-white/15 text-text-main rounded-bl-sm'}`}
                            data-testid={`message-${m.id}`}>
                            {getSoloIslamoji(m.content, islamojiMap) ? (
                              <img
                                src={getSoloIslamoji(m.content, islamojiMap).image_url}
                                alt={m.content}
                                className="w-[68px] h-[68px]"
                              />
                            ) : (
                              <p className="whitespace-pre-wrap break-words">{renderLineWithIslamojis(m.content, islamojiMap)}</p>
                            )}
                            <p className={`text-[10px] mt-1 ${mine ? 'text-white/70 text-right' : 'text-text-muted'}`}>
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {showIslamojis && (
                  <div className="px-3">
                    <IslamojiPicker onSelectEmoji={handleSelectIslamojiEmoji} showGifs={false} />
                  </div>
                )}
                <footer className="p-3 border-t border-primary/10 flex items-end gap-2">
                  <textarea ref={draftInputRef} value={draft} onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    rows={1} maxLength={1000} placeholder="Type a message…"
                    className="flex-1 resize-none px-4 py-2 rounded-2xl bg-white/70 dark:bg-white text-sm border border-primary/15 focus:ring-2 focus:ring-primary/40 dark:text-gray-800 max-h-32"
                    data-testid="message-input" />
                  <button onClick={() => setShowIslamojis(s => !s)}
                    aria-label="Add Islamoji"
                    className={`p-2.5 rounded-full transition-colors ${showIslamojis ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                    data-testid="message-islamoji-btn">
                    <Moon className="w-4 h-4" />
                  </button>
                  <button onClick={handleSend} disabled={sending || !draft.trim()}
                    className="p-2.5 rounded-full bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                    data-testid="message-send-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </footer>
                <p className="text-[10px] text-text-muted text-center pb-2 px-3">
                  {draft.length}/1000 · text only · be kind
                </p>
              </>
            )}
          </section>
        </motion.div>
      )}
    </div>
  );
};

export default MessagesPage;
